/**
 * Tiny IndexedDB wrapper for crash-safety: persists the import wizard's tagging
 * snapshot (small JSON from serializeTagging — NO file bytes) so a refresh/crash
 * before importing doesn't lose hours of tagging on a 1000-file pack.
 *
 * Everything is best-effort: if IndexedDB is unavailable/blocked (private mode,
 * old browser), all calls quietly no-op and the feature simply doesn't engage.
 */

const DB_NAME = "qdex-import";
const STORE = "sessions";
const keyFor = (workspaceId) => `session.${workspaceId || "default"}`;

function openDb() {
    return new Promise((resolve, reject) => {
        if (typeof indexedDB === "undefined") {
            reject(new Error("IndexedDB unavailable"));
            return;
        }
        const req = indexedDB.open(DB_NAME, 1);
        req.onupgradeneeded = () => {
            if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

function run(mode, fn) {
    return openDb().then(
        (db) =>
            new Promise((resolve, reject) => {
                const tx = db.transaction(STORE, mode);
                const store = tx.objectStore(STORE);
                const request = fn(store);
                tx.oncomplete = () => resolve(request ? request.result : undefined);
                tx.onerror = () => reject(tx.error);
                tx.onabort = () => reject(tx.error);
            })
    );
}

/** @param {object} snapshot — { folders, files, savedAt } */
export async function saveSnapshot(workspaceId, snapshot) {
    try {
        await run("readwrite", (s) => s.put(snapshot, keyFor(workspaceId)));
    } catch {
        /* best-effort */
    }
}

export async function loadSnapshot(workspaceId) {
    try {
        return await run("readonly", (s) => s.get(keyFor(workspaceId)));
    } catch {
        return null;
    }
}

export async function clearSnapshot(workspaceId) {
    try {
        await run("readwrite", (s) => s.delete(keyFor(workspaceId)));
    } catch {
        /* best-effort */
    }
}
